import {useTranslation} from 'react-i18next';

import {useModal} from '@hooks';
import {getBuildingById, useAppDispatch, useAppSelector, withAsyncThunkErrorHandling} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {OfficeWorkday, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {useEditWorkspaceReservation} from 'src/components/organisms/calendar/CalendarWeekDay/components/QuickOfficeDayButton_timeslots';
import {ModalPageBottom} from 'src/components/organisms/ModalPageBottom';
import {useAcceptDeclineWorkspaceInvitation_timeslots} from 'src/hooks/useAcceptDeclineWorkdayInvitation_timeslots';

type Props = {
  workday: OfficeWorkday | null;
  workspaceReservation: WorkspaceReservation;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Footer = ({workday, workspaceReservation, setIsSaving}: Props) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const {closeModal} = useModal();
  const {allowWorkdayWithoutWorkspace} =
    useAppSelector((state) => getBuildingById(state, workday?.buildingId || '')) ?? {};

  const editFlow = useEditWorkspaceReservation(workspaceReservation);
  const isPending = workspaceReservation.status === 'InvitationPending';
  const isConfirmed = workspaceReservation.status === 'Confirmed';
  const isInvitation = workspaceReservation.invitationTo !== null;

  const {isSaving, onAcceptInvitation, onDeclineInvitation} =
    useAcceptDeclineWorkspaceInvitation_timeslots(workspaceReservation);

  const rightButton: {label: string; onClick: () => void; destructive: boolean} = isPending
    ? {
        label: t('translation:Accept'),
        destructive: false,
        onClick: () => onAcceptInvitation(closeModal),
      }
    : isConfirmed
    ? {
        label: t('workplace:DeleteBooking'),
        destructive: true,
        onClick: async () => {
          setIsSaving(true);

          let result;
          if (allowWorkdayWithoutWorkspace || !workday) {
            result = await dispatch(
              withAsyncThunkErrorHandling(() => actions.cancelWorkspaceReservation({workspaceReservation})),
            );
          } else {
            result = await dispatch(
              withAsyncThunkErrorHandling(() => actions.cancelWorkdayIncludingReservations({workday})),
            );
          }

          setIsSaving(false);

          if (!result.success) {
            alert(result.result.message);
          } else {
            closeModal();
          }
        },
      }
    : {
        label: 'TODO: label',
        destructive: false,
        onClick: () => alert('TODO: handle this case'),
      };

  const leftButton: {label: string; onClick?: () => void; destructive: boolean} =
    workspaceReservation.status === 'InvitationPending'
      ? {
          label: t('translation:Decline'),
          destructive: true,
          onClick: () => onDeclineInvitation(closeModal),
        }
      : !isInvitation
      ? {
          label: t('Edit'),
          destructive: false,
          onClick: () => {
            if (!workday) throw new Error('Attempt to edit a reservation that has no workday');
            editFlow.openModal(workday);
          },
        }
      : {
          label: '',
          destructive: true,
          onClick: undefined,
        };

  return (
    <ModalPageBottom
      // Right button
      buttonLabel={rightButton.label}
      buttonType={rightButton.destructive ? 'primary destructive' : 'primary'}
      buttonDisabled={isSaving}
      destructive={rightButton.destructive}
      onClick={rightButton.onClick}
      // Left button
      cancelButtonLabel={leftButton.label}
      cancelButtonType={leftButton.destructive ? 'secondary' : 'tertiary'}
      cancelButtonDisabled={isSaving}
      onCancel={leftButton.onClick}
    />
  );
};
